import { useCallback, useEffect, useRef } from "react";

/**
 * Som de alerta de mensagem nova.
 *
 * Substitui `use-sound` + `howler`, que estavam quebrados nesta build: o
 * pré-bundle do Vite expõe o howler só como `default`, e o use-sound lê
 * `mod.Howl` — que vinha `undefined`. O resultado era
 * `TypeError: HowlConstructor.current is not a constructor` no console e
 * nenhum som tocando desde então. Nada disso aparecia na interface: o
 * alerta simplesmente não existia mais.
 *
 * O elemento <audio> nativo dá conta de tocar um mp3 curto e dispensa as
 * duas dependências.
 */
const useAlertSound = (src, { volume = 1 } = {}) => {
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = new Audio(src);
    audio.preload = "auto";
    audio.volume = volume;
    audioRef.current = audio;

    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, [src, volume]);

  return useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Sem o reset, uma segunda mensagem que chega antes do som terminar
    // não toca nada — o elemento já está em reprodução.
    audio.currentTime = 0;

    const attempt = audio.play();
    // O navegador recusa áudio antes de qualquer interação do usuário. É
    // esperado e não é erro nosso: engolir a rejeição evita poluir o
    // console com unhandled rejection a cada notificação.
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => {});
    }
  }, []);
};

export default useAlertSound;
